/**
 * Addressing — the slug rule, the board key and card reference grammar, and the
 * permalink shapes, for block B08 (`FR-EXP-015`, `FR-CARD-005`), amended by the
 * owner-directed board keys of 2026-10-08 (`docs/notes/card-keys.md`).
 *
 * WHY THIS IS ITS OWN MODULE rather than functions inside `./board`.
 * `./board` is a **contract** file: Zod schemas describing what B08's routes
 * accept and return, and nothing in it runs. This is the opposite — pure text
 * transformation with no Zod, no widths and no route. It is also not board-only:
 * `FR-EXP-015` gives boards and cards the same URL grammar, `FR-EXP-016`'s share
 * surface builds the same strings for both, and both halves of the product need
 * the rule. The server writes the canonical path into a resolution response; the
 * web client builds a link to a card it already holds without asking the
 * server. **One rule, one implementation** — two would drift on the first
 * rename, and `FR-BOARD-023`'s card-return state is keyed on the path string,
 * so a one-byte difference between the two producers silently loses it.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS PARSED, AND WHAT IS NOT
 * ---------------------------------------------------------------------------
 * A board card's path is `/c/<KEY-NNNN>/<slug>` — `MG-0001`, the board's key
 * and the card's board-scoped number. **The reference is parsed**
 * (`parseCardRef`), because it is what resolves: `GET /cards/by-short-link/…`
 * takes either a short link or a reference in the same segment, and the hyphen
 * tells them apart (a short link is base 62 and never holds one, so `MG0001` is
 * a short link and `MG-0001` is a reference).
 *
 * **The slug is still not parsed.** `FR-EXP-015` says it is "derived from the
 * title and ignored on lookup", and AC4 requires a path used without it to
 * resolve identically. The number no longer shares the slug's segment — the
 * old `/c/<shortLink>/<cardNumber>-<slug>` put both in one, which was ambiguous
 * for a title starting with a digit — so nothing after the reference is ever a
 * key.
 *
 * A card with no reference to give — a personal-inbox card has no board and no
 * number, and a reader who cannot resolve references (below) has none either —
 * keeps `/c/<shortLink>/<slug>`. The short link never changes and always
 * resolves, so it is the fallback every old link already uses.
 *
 * **Who may resolve a reference is narrower than who may read the card.** A
 * reference is enumerable (`MG-0001`…`MG-9999`) where a 12-character short link
 * is not, so it resolves only for a signed-in caller who reads the board
 * through membership or workspace/Enterprise visibility — never through the
 * public arm, never anonymously (`FR-EXP-015`, `FR-PERM-010`). A producer
 * therefore passes `ref: null` for a caller outside that scope, and the caller
 * gets a short-link path that resolves for them.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS DELIBERATELY ABSENT
 * ---------------------------------------------------------------------------
 *   - **Absolute URLs.** These are paths, not `https://…`. The origin depends on
 *     the deployment and nothing in this package knows it; `FR-EXP-016`'s "copy
 *     link" (V1) is where an origin has to come from, and it will prefix these.
 *   - **A workspace URL.** `FR-PERM-005` says the short name is "used in the
 *     workspace URL", and B07 derives the short name but no requirement in B07's
 *     scope states the path. Owned by `BE-39.1`.
 *   - **A stored-key derivation.** `suggestBoardKey` is a preview. The database
 *     derives every stored key itself (`molt_derive_board_key`, migration
 *     `0067`), so the rule cannot drift between two implementations of a value
 *     that is persisted.
 */

// ---------------------------------------------------------------------------
// The slug
// ---------------------------------------------------------------------------

/**
 * The cap on a derived slug, in characters.
 *
 * **Not a column width — no column stores a slug.** It is derived at read time
 * from `boards.title` / `cards.title`, both `varchar(512)`, and a 512-character
 * path segment is a URL nobody can paste into the places `FR-EXP-015` names
 * ("chat, docs and issue trackers") without it being folded, elided or line-
 * broken. Sixty-four is chosen as the length at which a slug still reads as a
 * title in a chat client's link preview while a full permalink
 * (`/c/` + 16 + `/` + digits + 64) stays under a hundred characters.
 *
 * Widening it later is free: the segment is ignored on lookup, so an old link
 * built against a shorter cap resolves identically.
 */
export const SLUG_MAX_LENGTH = 64;

/**
 * What a title that yields no slug becomes.
 *
 * `slugify` is **total**: it never returns the empty string. That is a hard
 * requirement rather than a nicety, because its output is interpolated into a
 * path — an empty return gives `/b/abc123/`, a trailing-slash variant of the
 * canonical address that every router and every link-shortener will normalise
 * differently, and `/c/abc123/`, which reads as a truncated URL and gets
 * repaired by clients that try to be helpful.
 *
 * It is reachable more often than it looks. `boards.title` and `cards.title`
 * carry no CHECK beyond `NOT NULL`, so "🚀🚀🚀", "…", "日本語のボード" and
 * "?!" are all legitimate titles, and none of them contributes a single
 * character this rule admits.
 *
 * The word is **not a claim that the title is empty.** It is the placeholder the
 * URL carries when the title contributes nothing addressable, and since the
 * segment is cosmetic and ignored on lookup (`FR-EXP-015`) it costs the user
 * nothing: the short link or reference before it is what resolves, and the client corrects
 * the address bar to whatever the server says is canonical — which, for such a
 * title, is this word.
 */
export const SLUG_FALLBACK = 'untitled';

/** The board URL prefix — `FR-EXP-015`: "`/b/<shortLink>/<slug>` for boards". */
export const BOARD_PATH_PREFIX = '/b';

/** The card URL prefix — `FR-EXP-015`: "`/c/<shortLink>/<slug>` for cards". */
export const CARD_PATH_PREFIX = '/c';

/** Hash prefix for a comment permalink target - B61, `FR-COLLAB-012`. */
export const COMMENT_ANCHOR_PREFIX = 'comment';

/** Hash prefix for an activity permalink target - B61, `FR-COLLAB-012`. */
export const ACTIVITY_ANCHOR_PREFIX = 'activity';

/**
 * Combining marks, stripped after an NFKD decomposition. This is the whole of
 * the transliteration this module performs — see `slugify`.
 */
const COMBINING_MARKS = /\p{M}+/gu;

/** Everything the slug alphabet does not admit. A **run** becomes one hyphen. */
const NON_SLUG = /[^a-z0-9]+/g;

const EDGE_HYPHENS = /^-+|-+$/g;

/**
 * Cap a non-empty slug, preferring a word boundary.
 *
 * The hard slice is taken first and then walked back to the last hyphen, so
 * "the-quick-brown-fox…" loses a whole word rather than ending in "fo". The
 * walk-back is skipped when the slice holds no hyphen — a single very long word
 * — because there is no boundary to prefer and returning the fallback instead
 * would throw away the only readable thing the title had.
 */
function capSlug(slug: string): string {
  if (slug.length <= SLUG_MAX_LENGTH) return slug;

  const hard = slug.slice(0, SLUG_MAX_LENGTH);
  const lastBoundary = hard.lastIndexOf('-');
  const cut = lastBoundary > 0 ? hard.slice(0, lastBoundary) : hard;

  return cut.replace(EDGE_HYPHENS, '');
}

/**
 * A title reduced to a URL path segment. Total, deterministic, and never empty.
 *
 * The rule, in order: NFKD-decompose, drop combining marks, lower-case, replace
 * every run of characters outside `[a-z0-9]` with a single hyphen, trim hyphens
 * off both ends, cap at `SLUG_MAX_LENGTH` on a word boundary, and fall back to
 * `SLUG_FALLBACK` if nothing survives.
 *
 * ---------------------------------------------------------------------------
 * UNICODE: DECOMPOSE, THEN STRIP — AND NO TRANSLITERATION TABLE
 * ---------------------------------------------------------------------------
 * The NFKD pass is the *only* transliteration, and it is here because it is not
 * a language decision: "Café" → "cafe" and "Añejo" → "anejo" fall out of the
 * Unicode standard's own decomposition, so nothing is being guessed. Everything
 * beyond that is stripped, and that is a decision with two alternatives, both
 * rejected:
 *
 *   - **A transliteration table** (Cyrillic → Latin, CJK → romanisation) is a
 *     claim about which language a title is in, and it is wrong roughly as often
 *     as it is right: 中 is *zhōng* in Mandarin and *naka* or *chū* in Japanese,
 *     and the same string of characters can be either. A slug that asserts the
 *     wrong reading is worse than one that says nothing, because it is a
 *     confident mislabel that the member sees in their own address bar. It also
 *     means a dependency and a data table, for a segment that is ignored on
 *     lookup.
 *   - **Keeping the characters, percent-encoded.** Legal, and what some products
 *     do. Rejected because `FR-EXP-015`'s whole user story is pasting the URL
 *     "anywhere" — chat, docs, issue trackers — and a percent-encoded CJK
 *     segment triples the length of the link and renders as noise in every one
 *     of those places. The slug's only job is to be readable; when it cannot be,
 *     it should be absent rather than long.
 *
 * A known and accepted wart of stripping: characters that NFKD does **not**
 * decompose but that a human would transliterate — ß, ø, æ, đ, ł — become
 * hyphens, so "Straße" slugs as `stra-e`. Fixing that means starting the very
 * table the paragraph above rejects (ß → "ss" is German; æ → "ae" or "e" depends
 * on the language), and the cost of the wart is a cosmetic segment nobody
 * parses.
 *
 * Case is folded because a URL path is compared case-sensitively by some proxies
 * and caches and not by others, and a slug that differs only in case would make
 * two spellings of the same canonical address. This is safe **only** because the
 * slug is not a key; the short link or reference beside it is, and `shortLinkSchema` in
 * `./board` deliberately does not fold case for exactly the opposite reason.
 */
export function slugify(title: string): string {
  const folded = title.normalize('NFKD').replace(COMBINING_MARKS, '').toLowerCase();
  const hyphenated = folded.replace(NON_SLUG, '-').replace(EDGE_HYPHENS, '');

  if (hyphenated.length === 0) return SLUG_FALLBACK;

  return capSlug(hyphenated);
}

// ---------------------------------------------------------------------------
// The board key and the card reference — `docs/notes/card-keys.md`
// ---------------------------------------------------------------------------

/**
 * A board key as `boards.card_key` stores it: an upper-case letter and one to
 * nine more upper-case letters or digits. The same expression as
 * `ck_boards_card_key` and `ck_board_key_history_card_key` (migration `0067`).
 */
export const BOARD_CARD_KEY_PATTERN = /^[A-Z][A-Z0-9]{1,9}$/;

/** `boards.card_key varchar(10)`. */
export const BOARD_CARD_KEY_MAX_LENGTH = 10;

/**
 * The digits a reference's number is padded to. **Padded, never truncated**:
 * card 10,000 is `MG-10000`, not `MG-0000`.
 */
export const CARD_REF_PAD = 4;

/**
 * The largest number a reference can carry — `cards.card_number` is an
 * `integer`. Larger is not a reference at all: binding it against the column
 * would be a Postgres range error (a 500), not the 404 a reference that names
 * nothing must answer.
 */
export const CARD_REF_NUMBER_MAX = 2_147_483_647;

/**
 * The reference grammar. **The hyphen is required** — `MG0001` is a valid
 * base-62 short link and must stay one. Leading zeros are admitted and dropped
 * (`MG-0001` and `MG-1` name the same card); the key is matched in either case
 * and upper-cased, because a reference is typed by hand.
 */
const CARD_REF_PATTERN = /^([A-Za-z][A-Za-z0-9]{1,9})-0*([1-9][0-9]*)$/;

/** `formatCardRef('MG', 1)` → `MG-0001`; `formatCardRef('MG', 10000)` → `MG-10000`. */
export function formatCardRef(key: string, cardNumber: number): string {
  return `${key}-${String(cardNumber).padStart(CARD_REF_PAD, '0')}`;
}

/**
 * The reference a card has, or `null` when there is none to give: no key (a
 * personal-inbox card, or a caller who cannot resolve references on its board)
 * or no number. The one place a producer turns the two halves into a
 * `CardPathAddress.ref`.
 */
export function cardRefFor(key: string | null, cardNumber: number | null): string | null {
  return key === null || cardNumber === null ? null : formatCardRef(key, cardNumber);
}

/** A parsed reference: the upper-cased key and the board-scoped number. */
export interface CardRef {
  readonly key: string;
  readonly number: number;
}

/**
 * `MG-0001` → `{ key: 'MG', number: 1 }`; anything that is not a reference →
 * `null`, including `MG0001` (a short link), a number of zero and a number past
 * `CARD_REF_NUMBER_MAX`.
 */
export function parseCardRef(value: string): CardRef | null {
  const match = CARD_REF_PATTERN.exec(value);
  if (match === null) return null;

  const [, key, digits] = match;
  if (key === undefined || digits === undefined) return null;
  // Length first: `Number` rounds a long digit run, and a rounded value could
  // land back under the ceiling.
  if (digits.length > String(CARD_REF_NUMBER_MAX).length) return null;
  const number = Number(digits);
  if (number > CARD_REF_NUMBER_MAX) return null;

  return { key: key.toUpperCase(), number };
}

/**
 * Letters NFD does not decompose but `molt_board_key_base` spells out — the
 * same pairs as the SQL function's `replace` calls and its `translate`.
 */
const KEY_SPELLED_OUT: readonly (readonly [string, string])[] = [
  ['ß', 'ss'],
  ['ẞ', 'SS'],
  ['æ', 'ae'],
  ['Æ', 'AE'],
  ['œ', 'oe'],
  ['Œ', 'OE'],
  ['þ', 'th'],
  ['Þ', 'TH'],
  ['ø', 'o'],
  ['Ø', 'O'],
  ['ł', 'l'],
  ['Ł', 'L'],
  ['đ', 'd'],
  ['Đ', 'D'],
  ['ð', 'd'],
  ['Ð', 'D'],
  ['ı', 'i'],
];

/** U+0300–U+036F only, as the SQL strips — not `\p{M}`, which is wider. */
const KEY_COMBINING_MARKS = /[̀-ͯ]/g;

/** The key base for a title that offers no Latin letter to start one. */
export const BOARD_CARD_KEY_FALLBACK = 'BRD';

/**
 * The key base a board titled `title` would be given — **a preview only**, for
 * the board settings key editor's placeholder. It mirrors `molt_board_key_base`
 * (migration `0067`) step for step: fold to ASCII and break words on anything
 * else; two or more words give their initials (up to four, a leading
 * digit-word skipped), one word its first three characters, and `BRD` when no
 * word starts with a letter; upper-cased.
 *
 * It is the **base**, not the stored key: the database appends `2`, `3`, … when
 * the base is taken, and only the database knows that. Its unit table is the
 * integration table in `card-keys.integration.test.ts`, row for row, so the two
 * implementations cannot drift without one of them going red.
 */
export function suggestBoardKey(title: string): string {
  let folded = title.normalize('NFD').replace(KEY_COMBINING_MARKS, '');
  for (const [from, to] of KEY_SPELLED_OUT) folded = folded.split(from).join(to);
  // ASCII before upper-casing, as the SQL does: the two `upper`s disagree only
  // outside ASCII.
  folded = folded
    .replace(/[^A-Za-z0-9]+/g, ' ')
    .trim()
    .toUpperCase();

  if (folded === '' || !/(^| )[A-Z]/.test(folded)) return BOARD_CARD_KEY_FALLBACK;

  const words = folded.split(' ');
  let base = '';

  if (words.length >= 2) {
    for (const word of words) {
      if (base.length === 4) break;
      if (/^[A-Z]/.test(word) || base !== '') base += word.charAt(0);
    }
  }

  if (base.length < 2) {
    base = (words.find((word) => /^[A-Z]/.test(word)) ?? '').slice(0, 3);
  }

  return base.length < 2 ? BOARD_CARD_KEY_FALLBACK : base;
}

// ---------------------------------------------------------------------------
// The permalinks
// ---------------------------------------------------------------------------

/**
 * A board's canonical path — `FR-EXP-015`: "`/b/<shortLink>/<slug>` for boards".
 *
 * Takes the **slug**, not the title, so a caller holding a board view
 * (`boardSchema` carries `slug`) cannot produce a path that disagrees with the
 * slug the server just told them was canonical. Slugify the title yourself if
 * that is genuinely what you have.
 */
export function boardPath(shortLink: string, slug: string): string {
  return `${BOARD_PATH_PREFIX}/${shortLink}/${slug}`;
}

/**
 * What a card path is built from: the reference when the caller has one to
 * use, and the short link, which is the fallback and always resolves.
 *
 * `ref` is **required and nullable** rather than optional, so every producer
 * states which it means — `null` for an inbox card (no board, no number) and
 * for a caller who cannot resolve references (see the module header), never by
 * leaving a field out.
 */
export interface CardPathAddress {
  /** `KEY-NNNN`, from `cardRefFor` / `formatCardRef`, or `null`. */
  readonly ref: string | null;
  readonly shortLink: string;
}

/**
 * A card's canonical path — `/c/<KEY-NNNN>/<slug>` for a board card the caller
 * can reference, `/c/<shortLink>/<slug>` otherwise (`FR-CARD-005`,
 * `FR-EXP-015`, amended by `docs/notes/card-keys.md`).
 *
 * A cross-board move gives the card a new number on its new board, so the
 * reference in an old link is stale. It still resolves — `card_refs` keeps
 * every number ever issued — and the client corrects the address bar to the
 * path the resolver returns. The short link never changes, so an old
 * short-link path resolves too (`FR-EXP-015` AC3).
 */
export function cardPath(address: CardPathAddress, slug: string): string {
  return `${CARD_PATH_PREFIX}/${address.ref ?? address.shortLink}/${slug}`;
}

/**
 * The path segment that marks a comment-number address — `/c/<ref>/com/<n>`.
 */
export const CARD_COMMENT_NUMBER_SEGMENT = 'com';

/**
 * A card's address scoped to one of its comments **by number** —
 * `/c/MID-3245/com/3` opens card `MID-3245` and scrolls to its comment `#3`.
 *
 * The number is `comments.number`, which starts at 1 on each card and is never
 * reused (`docs/notes/comment-numbers.md`); it is a name for people, and the
 * id-keyed `#comment-<id>` permalink stays the stable machine address. The
 * segment is the card's reference when it has one, its short link otherwise,
 * exactly as in `cardPath`. There is no slug: `com` sits where the slug would.
 */
export function cardCommentNumberPath(
  address: Pick<CardPathAddress, 'ref' | 'shortLink'>,
  commentNumber: number,
): string {
  return `${CARD_PATH_PREFIX}/${address.ref ?? address.shortLink}/${CARD_COMMENT_NUMBER_SEGMENT}/${commentNumber}`;
}

/**
 * The hash target for a comment permalink.
 *
 * The id after the prefix is the only target id. The board/card slug around it
 * is decoration exactly like the existing board and card paths; no caller may
 * parse the anchor or slug back into ids.
 */
export function commentAnchor(commentId: string): string {
  return `${COMMENT_ANCHOR_PREFIX}-${commentId}`;
}

/**
 * The hash target for an activity permalink.
 *
 * Activity ids live in the anchor for both board-scoped and card-scoped
 * activity links, so the caller chooses the appropriate base path and the
 * target grammar stays one rule.
 */
export function activityAnchor(activityEntryId: string): string {
  return `${ACTIVITY_ANCHOR_PREFIX}-${activityEntryId}`;
}

/** A card path with a comment hash target appended. */
export function commentPath(
  address: CardPathAddress,
  slug: string,
  commentId: string,
): string {
  return `${cardPath(address, slug)}#${commentAnchor(commentId)}`;
}

/** A board path with a board-scoped activity hash target appended. */
export function boardActivityPath(
  shortLink: string,
  slug: string,
  activityEntryId: string,
): string {
  return `${boardPath(shortLink, slug)}#${activityAnchor(activityEntryId)}`;
}

/** A card path with a card-scoped activity hash target appended. */
export function cardActivityPath(
  address: CardPathAddress,
  slug: string,
  activityEntryId: string,
): string {
  return `${cardPath(address, slug)}#${activityAnchor(activityEntryId)}`;
}
